import {SecureDocumentKind, SecureViewerDocumentRef} from './types'

const MAX_WATERMARKED_IMAGE_EDGE_PX = 2800

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value))

const normalizeWatermarkText = (value?: string | null): string | null => {
  const normalized = value?.trim()
  return normalized ? normalized.toUpperCase() : null
}

const drawDocumentWatermark = (
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  watermarkText?: string | null
) => {
  const normalizedText = normalizeWatermarkText(watermarkText)

  if (!normalizedText) {
    return
  }

  const diagonalLength = Math.sqrt(canvas.width * canvas.width + canvas.height * canvas.height)
  const baseFontSize = clamp(Math.floor(Math.min(canvas.width, canvas.height) / 34), 11, 24)
  let fontSize = baseFontSize

  context.save()
  context.font = `700 ${fontSize}px Arial, sans-serif`

  let measuredWidth = context.measureText(normalizedText).width
  const maxTextWidth = Math.max(1, canvas.width * 0.78)

  if (measuredWidth > maxTextWidth) {
    fontSize = Math.max(9, Math.floor((fontSize * maxTextWidth) / measuredWidth))
    context.font = `700 ${fontSize}px Arial, sans-serif`
    measuredWidth = context.measureText(normalizedText).width
  }

  context.translate(canvas.width / 2, canvas.height / 2)
  context.rotate((-24 * Math.PI) / 180)
  context.fillStyle = 'rgba(13, 110, 253, 0.16)'
  context.textAlign = 'center'
  context.textBaseline = 'middle'

  const xStep = Math.max(measuredWidth + fontSize * 8, canvas.width * 0.7)
  const yStep = Math.max(fontSize * 7, canvas.height * 0.22)

  for (let y = -diagonalLength; y <= diagonalLength; y += yStep) {
    for (let x = -diagonalLength; x <= diagonalLength; x += xStep) {
      context.fillText(normalizedText, x, y)
    }
  }

  context.restore()
}

const loadImageFromBlob = async (blob: Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob)
    const image = new Image()

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(image)
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Preview unavailable'))
    }

    image.src = objectUrl
  })

const canvasToBlob = async (
  canvas: HTMLCanvasElement,
  contentType?: string | null
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const normalizedType = (contentType ?? '').toLowerCase()

    const outputType =
      normalizedType === 'image/jpeg' || normalizedType === 'image/webp'
        ? normalizedType
        : 'image/png'

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Preview unavailable'))
          return
        }

        resolve(blob)
      },
      outputType,
      0.92
    )
  })

export const resolveSecureDocumentKind = (
  document?: SecureViewerDocumentRef,
  fetchedContentType?: string | null
): SecureDocumentKind => {
  const normalizedType = (fetchedContentType || document?.contentType || '').toLowerCase().trim()

  if (normalizedType.startsWith('image/')) {
    return 'image'
  }

  if (normalizedType.includes('pdf')) {
    return 'pdf'
  }

  const normalizedFilename = (document?.filename || '').toLowerCase().trim()

  if (
    normalizedFilename.endsWith('.png') ||
    normalizedFilename.endsWith('.jpg') ||
    normalizedFilename.endsWith('.jpeg')
  ) {
    return 'image'
  }

  if (normalizedFilename.endsWith('.pdf')) {
    return 'pdf'
  }

  return 'unsupported'
}

export const renderPdfAsImages = async (
  blob: Blob,
  targetWidthPx: number,
  maxPageCount: number = 20,
  watermarkText?: string | null
): Promise<string[]> => {
  /*
   * PDF.js 4+/5+/6+ no longer uses the old:
   *
   *   pdf.worker.entry
   *
   * imports.
   *
   * Import the legacy API for compatibility with the existing
   * rendering code, but let PDF.js use its current worker implementation.
   */
  const pdfjsModule = await import('pdfjs-dist/legacy/build/pdf.mjs')

  /*
   * In a browser/Webpack application, PDF.js needs a worker.
   *
   * Using import.meta.url allows the browser to resolve the worker
   * module relative to the installed pdfjs-dist package.
   */
  pdfjsModule.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/legacy/build/pdf.worker.mjs',
    import.meta.url
  ).toString()

  const arrayBuffer = await blob.arrayBuffer()

  const loadingTask = pdfjsModule.getDocument({
    data: new Uint8Array(arrayBuffer),
  })

  const document = await loadingTask.promise

  const maxPages = Math.min(document.numPages || 0, maxPageCount)
  const pages: string[] = []

  for (let index = 1; index <= maxPages; index += 1) {
    const page = await document.getPage(index)

    const baseViewport = page.getViewport({
      scale: 1,
    })

    const computedScale = Math.max(
      0.35,
      Math.min(3, targetWidthPx / Math.max(1, baseViewport.width))
    )

    const viewport = page.getViewport({
      scale: computedScale,
    })

    const canvas = window.document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) {
      continue
    }

    canvas.width = Math.max(1, Math.floor(viewport.width))
    canvas.height = Math.max(1, Math.floor(viewport.height))

    await page.render({
      canvas,
      canvasContext: context,
      viewport,
    }).promise
    drawDocumentWatermark(context, canvas, watermarkText)

    pages.push(canvas.toDataURL('image/png'))
  }

  return pages
}

export const renderImageWithWatermark = async (
  blob: Blob,
  watermarkText?: string | null,
  contentType?: string | null
): Promise<Blob> => {
  if (!normalizeWatermarkText(watermarkText)) {
    return blob
  }

  const image = await loadImageFromBlob(blob)

  const sourceWidth = image.naturalWidth || image.width
  const sourceHeight = image.naturalHeight || image.height

  if (sourceWidth <= 0 || sourceHeight <= 0) {
    throw new Error('Preview unavailable')
  }

  const scale = Math.min(1, MAX_WATERMARKED_IMAGE_EDGE_PX / Math.max(sourceWidth, sourceHeight))

  const canvas = window.document.createElement('canvas')
  const context = canvas.getContext('2d')

  if (!context) {
    throw new Error('Preview unavailable')
  }

  canvas.width = Math.max(1, Math.floor(sourceWidth * scale))
  canvas.height = Math.max(1, Math.floor(sourceHeight * scale))

  context.drawImage(image, 0, 0, canvas.width, canvas.height)

  drawDocumentWatermark(context, canvas, watermarkText)

  return canvasToBlob(canvas, contentType)
}
